import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MessageCircle, 
  Calendar, 
  ArrowRight,
  Activity,
  Apple,
  Clock,
  Users,
  Check,
  ChevronRight,
  BookOpen,
  PlayCircle,
  Heart,
  Target,
  TrendingUp,
  Quote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, blogPosts, videos, contactInfo, siteConfig } from '../lib/data';
import PageMeta from '../components/PageMeta';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.12
    }
  }
};

// Enhanced service descriptions
const serviceDescriptions = {
  'injury-sports-treatments': 'Hands-on therapy combining neuromuscular techniques and dry needling to reduce pain, restore mobility, and accelerate recovery from sports injuries and musculoskeletal conditions.',
  'nutritional-therapy': 'Personalised nutrition support that addresses the root causes of digestive issues, hormonal imbalances, fatigue, and chronic health concerns through evidence-based dietary strategies.',
  'once-off-services': 'Targeted single-session consultations for specific health concerns, food intolerance testing, or a comprehensive mini health review to guide your wellness journey.',
  'talks-workshops': 'Engaging group sessions for sports teams, workplaces, and community organisations covering nutrition, injury prevention, and practical wellness strategies.',
};

// Section 1: Hero - UPGRADED
const HeroSection = () => (
  <section className="relative overflow-hidden bg-cream-100 min-h-[auto] lg:min-h-[90vh] flex items-center py-8 lg:py-0" data-testid="hero-section">
    {/* Subtle background pattern */}
    <div className="absolute inset-0 opacity-[0.015]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232D2D2D' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }} />
    
    <div className="container-custom relative z-10">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-24 items-center">
        {/* Left column — heading + (desktop: subheadline + CTAs) */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="text-left"
        >
          {/* Headline */}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light text-charcoal leading-[1.1] mb-6 lg:mb-8" data-testid="hero-heading">
            Nutrition & Sports
            <span className="block">Injuries Clinic</span>
            <span className="block text-sage-600 mt-2">in Cobh, Cork</span>
          </h1>
          
          {/* Desktop-only subheadline + CTAs */}
          <div className="hidden lg:block">
            <div className="w-16 h-[2px] bg-sage-400 mb-8" />
            <p className="text-xl md:text-2xl text-charcoal/70 mb-8 leading-relaxed max-w-xl font-light">
              Specialist hands-on therapy for injury recovery, pain relief, and performance — 
              backed by personalised nutrition support.
            </p>
            <p className="text-sm text-sage-600 font-medium mb-10 tracking-wide border-l-2 border-sage-400 pl-4">
              {siteConfig.credentials}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full px-10 py-6 gap-2 shadow-soft hover:shadow-medium hover:-translate-y-0.5 transition-all duration-300 text-base"
                data-testid="hero-whatsapp-btn"
              >
                <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5" />
                  Message Ali
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full px-10 py-6 border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-white hover:-translate-y-0.5 transition-all duration-300 text-base"
                data-testid="hero-book-btn"
              >
                <Link to="/services/injury-sports#practitioners">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Injury & Sports Treatment
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
        
        {/* Right column — hero image (visible on both mobile and desktop) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
        >
          <div className="rounded-2xl lg:rounded-3xl overflow-hidden shadow-medium mx-auto max-w-xs sm:max-w-sm lg:max-w-none">
            <img
              src="/ali-hero.jpg"
              alt="Ali Dorgan — Neuromuscular & Nutritional Therapist"
              className="w-full h-auto object-cover max-h-[50vh] lg:max-h-[600px]"
              loading="eager"
              data-testid="hero-image"
            />
          </div>
        </motion.div>
      </div>

      {/* Mobile-only subheadline + CTAs (below the image) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="lg:hidden mt-8"
      >
        <div className="w-16 h-[2px] bg-sage-400 mb-6" />
        <p className="text-lg text-charcoal/70 mb-6 leading-relaxed font-light">
          Specialist hands-on therapy for injury recovery, pain relief, and performance — 
          backed by personalised nutrition support.
        </p>
        <p className="text-sm text-sage-600 font-medium mb-8 tracking-wide border-l-2 border-sage-400 pl-4">
          {siteConfig.credentials}
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            asChild
            size="lg"
            className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full px-10 py-6 gap-2 shadow-soft hover:shadow-medium hover:-translate-y-0.5 transition-all duration-300 text-base"
            data-testid="hero-whatsapp-btn"
          >
            <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="w-5 h-5" />
              Message Ali
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="rounded-full px-10 py-6 border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-white hover:-translate-y-0.5 transition-all duration-300 text-base"
            data-testid="hero-book-btn"
          >
            <Link to="/services/injury-sports#practitioners">
              <Calendar className="w-5 h-5 mr-2" />
              Book Injury & Sports Treatment
            </Link>
          </Button>
        </div>
      </motion.div>
    </div>
  </section>
);

// Section 2: Authority & Background
const AuthoritySection = () => (
  <section className="py-24 md:py-32 lg:py-40 bg-white" data-testid="authority-section">
    <div className="container-custom">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Placeholder for Ali's photo */}
          <div className="rounded-2xl lg:rounded-3xl overflow-hidden shadow-soft max-w-lg mx-auto lg:mx-0">
            <img
              src="/ali-therapy.jpg"
              alt="Ali Dorgan performing neuromuscular therapy"
              className="w-full h-auto object-cover"
              loading="lazy"
              data-testid="authority-image"
            />
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
            About Ali
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal mb-8 leading-tight">
            A Passion for Wellbeing, Rooted in Experience
          </h2>
          <div className="space-y-5 text-charcoal/70 leading-relaxed text-lg">
            <p>
              Ali&apos;s passion for wellbeing comes from her background in competitive sports. 
              She qualified in 2003 with a degree in Social Science and completed her Injury 
              & Sports Treatments qualification in 2005.
            </p>
            <p>
              With a strong interest in nutrition, she undertook a 3-year Nutritional Therapy 
              qualification, graduating in 2018. She also holds qualifications in Advanced 
              Dry Needling and uses a hands-on approach in her treatments.
            </p>
            <p>
              We work closely with clients to create long-term improvements in health, 
              guiding, supporting, and educating them throughout the process.
            </p>
          </div>
          <Link 
            to="/about" 
            className="inline-flex items-center gap-2 mt-8 text-sage-600 hover:text-sage-700 font-semibold transition-colors group"
            data-testid="about-link"
          >
            Learn more about Ali
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);

// Section 3: Services Overview - ENHANCED
const ServicesSection = () => {
  const serviceIcons = {
    'injury-sports-treatments': Activity,
    'nutritional-therapy': Apple,
    'once-off-services': Clock,
    'talks-workshops': Users,
  };

  return (
    <section className="py-24 md:py-32 lg:py-40 bg-cream-100" data-testid="services-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
            Services
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">
            How We Can Help You
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {services.map((service) => {
            const IconComponent = serviceIcons[service.id] || Activity;
            const enhancedDescription = serviceDescriptions[service.id] || service.shortDescription;
            return (
              <motion.div key={service.id} variants={fadeInUp}>
                <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium hover:-translate-y-2 transition-all duration-500 group rounded-2xl overflow-hidden">
                  <CardContent className="p-8 lg:p-10 flex flex-col h-full">
                    <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-sage-200 group-hover:scale-110 transition-all duration-300">
                      <IconComponent className="w-8 h-8 text-sage-600" />
                    </div>
                    <h3 className="font-serif text-2xl font-medium text-charcoal mb-4">
                      {service.title}
                    </h3>
                    <p className="text-charcoal/60 text-base leading-relaxed flex-1 mb-8">
                      {enhancedDescription}
                    </p>
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center gap-2 text-sage-600 hover:text-sage-700 font-semibold text-sm transition-colors group/link"
                      data-testid={`service-link-${service.slug}`}
                    >
                      Learn More
                      <ChevronRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// NEW Section: Why Choose Ali
const WhyChooseSection = () => {
  const reasons = [
    {
      icon: Heart,
      title: 'Individualised Treatment',
      description: 'Every treatment is tailored to the individual — assessing the root cause of pain and dysfunction, not just addressing symptoms.',
    },
    {
      icon: Target,
      title: 'Evidence-Informed Practice',
      description: 'All treatment is based on current research, extensive clinical experience, and practical real-world application in sport and injury.',
    },
    {
      icon: TrendingUp,
      title: 'Long-Term Recovery',
      description: 'Focus on lasting improvement and injury prevention, not quick fixes. Building strength and resilience for the long term.',
    },
  ];

  return (
    <section className="py-24 md:py-32 lg:py-40 bg-white" data-testid="why-choose-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
            The Difference
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">
            Why Choose Ali
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-10 lg:gap-16"
        >
          {reasons.map((reason, index) => (
            <motion.div 
              key={reason.title} 
              variants={fadeInUp}
              className="text-center"
            >
              <div className="w-20 h-20 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <reason.icon className="w-10 h-10 text-sage-600" />
              </div>
              <h3 className="font-serif text-2xl font-medium text-charcoal mb-4">
                {reason.title}
              </h3>
              <p className="text-charcoal/60 text-base leading-relaxed max-w-sm mx-auto">
                {reason.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Section 4: What We Help With
const ConditionsSection = () => {
  const conditions = [
    'Sports injuries',
    'Post-injury recovery',
    'Chronic pain',
    'Performance optimisation',
    'Muscle & joint pain',
    'Back & neck issues',
    'Digestive issues',
    'Fatigue & low energy',
  ];

  return (
    <section className="py-24 md:py-32 bg-sage-900" data-testid="conditions-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-sage-400 font-semibold mb-6 block">
            Areas of Focus
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-cream-100">
            What We Help With
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto"
        >
          {conditions.map((condition) => (
            <motion.div
              key={condition}
              variants={fadeInUp}
              className="flex items-center gap-3 bg-sage-800/60 rounded-xl px-5 py-4 hover:bg-sage-800 transition-colors"
            >
              <Check className="w-5 h-5 text-sage-400 flex-shrink-0" />
              <span className="text-cream-100 text-sm font-medium">{condition}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Section 5: How It Works
const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Get in Touch or Book',
      description: 'Reach out via WhatsApp or book an appointment online. We will respond personally to discuss your needs.',
    },
    {
      number: '02',
      title: 'Assessment & Personalised Treatment',
      description: 'A thorough assessment to understand your concerns, followed by a tailored treatment plan designed for your specific goals.',
    },
    {
      number: '03',
      title: 'Ongoing Support & Progress',
      description: 'Continue your journey with ongoing support, progress tracking, and adjustments to optimise your results.',
    },
  ];

  return (
    <section className="py-24 md:py-32 lg:py-40 bg-cream-50" data-testid="process-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
            The Process
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">
            How It Works
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-12 lg:gap-16"
        >
          {steps.map((step) => (
            <motion.div key={step.number} variants={fadeInUp} className="text-center">
              <div className="inline-block mb-8">
                <span className="font-serif text-7xl lg:text-8xl font-light text-sage-200">
                  {step.number}
                </span>
              </div>
              <h3 className="font-serif text-2xl font-medium text-charcoal mb-4">
                {step.title}
              </h3>
              <p className="text-charcoal/60 text-base leading-relaxed max-w-xs mx-auto">
                {step.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Section 6: Pricing Overview - IMPROVED
const PricingSection = () => {
  const pricing = [
    { service: 'Physical Therapy', duration: '1 hour', price: '€65', popular: true },
    { service: 'Physical Therapy', duration: '30 minutes', price: '€40', popular: false },
    { service: 'Nutritional Therapy Package', duration: '3 sessions', price: '€250', popular: false },
    { service: 'Food Intolerance Testing', duration: '', price: '€110', popular: false },
    { service: 'Mini Health Review', duration: '', price: '€80', popular: false },
    { service: 'Talks & Workshops', duration: '', price: '€250', popular: false },
  ];

  return (
    <section className="py-24 md:py-32 lg:py-40 bg-white" data-testid="pricing-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
            Investment
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal mb-4">
            Pricing Overview
          </h2>
          <p className="text-charcoal/60 text-lg max-w-xl mx-auto">
            Transparent pricing for all services. No hidden fees.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
        >
          {pricing.map((item, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <Card className={`h-full border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl overflow-hidden ${item.popular ? 'bg-sage-50 ring-2 ring-sage-200' : 'bg-cream-50'}`}>
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-charcoal text-lg">{item.service}</h3>
                      {item.duration && (
                        <p className="text-charcoal/50 text-sm mt-1">{item.duration}</p>
                      )}
                    </div>
                    {item.popular && (
                      <span className="bg-sage-600 text-white text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap shrink-0">
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="mt-6">
                    <span className="font-serif text-4xl text-sage-600 whitespace-nowrap">{item.price}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button
            asChild
            size="lg"
            className="rounded-full px-10 py-6 bg-charcoal text-white hover:bg-charcoal/90 hover:-translate-y-0.5 transition-all duration-300"
            data-testid="pricing-services-btn"
          >
            <Link to="/services/injury-sports">
              View All Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Section 7: Testimonial Preview - REAL CONTENT
const TestimonialSection = () => {
  const homeTestimonials = [
    {
      id: 1,
      quote: "I have been going to Ali for years , wouldn't go past her , she is the best at Dry Needling and by far the most thorough",
      name: "Brendan",
      detail: "Cobh",
    },
    {
      id: 4,
      quote: "I went to Ali after I was diagnosed with Non-alcohol Fatty Liver disease and high cholesterol. I had not been feeling myself for months before going to Ali. What struck me from my first appointment was how knowledgeable Ali was! My treatment was so specific to me and easy to follow. Ali's support and guidance were very reassuring throughout. In a few short months under Ali's guidance I was able to reverse my Fatty Liver diagnosis and reduce my cholesterol. I have energy now that I haven't had in years and I can honestly say I feel 10 years younger. I cannot recommend Ali highly enough.",
      name: "Niamh",
      detail: "Cobh, Co Cork",
    },
    {
      id: 5,
      quote: "Ali has given me so much knowledge on how I can improve my health and guided me on managing my diabetes with food changes and exercise to avoid going on medication",
      name: "Christine",
      detail: "Cobh",
    },
  ];

  return (
    <section className="py-24 md:py-32 lg:py-40 bg-cream-100" data-testid="testimonial-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
            Client Experiences
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">
            What Clients Say
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {homeTestimonials.map((testimonial, i) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
            >
              <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl">
                <CardContent className="p-10 flex flex-col h-full">
                  <Quote className="w-10 h-10 text-sage-200 mb-6 shrink-0" />
                  <p className="text-charcoal/70 text-base leading-relaxed flex-1 mb-8 line-clamp-6">
                    "{testimonial.quote}"
                  </p>
                  <div className="border-t border-border/40 pt-6">
                    <p className="text-charcoal font-semibold">{testimonial.name}</p>
                    <p className="text-charcoal/50 text-sm mt-1">{testimonial.detail}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-charcoal/50 text-sm mt-12 italic">
          Individual results may vary.
        </p>

        <div className="text-center mt-8">
          <Button
            asChild
            variant="ghost"
            className="text-sage-600 hover:text-sage-700 hover:bg-sage-100 rounded-full px-6"
            data-testid="testimonials-link"
          >
            <Link to="/testimonials">
              View All Testimonials
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

// Section 8: Resources Preview
const ResourcesSection = () => (
  <section className="py-24 md:py-32 lg:py-40 bg-sage-50" data-testid="resources-section">
    <div className="container-custom">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
          Learn More
        </span>
        <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">
          Resources
        </h2>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
        {/* Blog Preview */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-300 group rounded-2xl">
            <CardContent className="p-10">
              <div className="w-14 h-14 bg-sage-100 rounded-xl flex items-center justify-center mb-8 group-hover:bg-sage-200 transition-colors">
                <BookOpen className="w-7 h-7 text-sage-600" />
              </div>
              <h3 className="font-serif text-3xl font-medium text-charcoal mb-4">Blog</h3>
              <p className="text-charcoal/60 text-base leading-relaxed mb-8">
                Articles on nutrition, therapy techniques, injury prevention, and wellness tips to support your health journey.
              </p>
              <div className="space-y-4 mb-8">
                {blogPosts.slice(0, 2).map((post) => (
                  <Link
                    key={post.id}
                    to={`/resources/blog/${post.id}`}
                    className="block text-base text-charcoal hover:text-sage-600 transition-colors font-medium"
                  >
                    {post.title}
                  </Link>
                ))}
              </div>
              <Link
                to="/resources/blog"
                className="inline-flex items-center gap-2 text-sage-600 hover:text-sage-700 font-semibold transition-colors group/link"
                data-testid="blog-link"
              >
                View All Articles
                <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </CardContent>
          </Card>
        </motion.div>

        {/* Video Preview */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-300 group rounded-2xl">
            <CardContent className="p-10">
              <div className="w-14 h-14 bg-sage-100 rounded-xl flex items-center justify-center mb-8 group-hover:bg-sage-200 transition-colors">
                <PlayCircle className="w-7 h-7 text-sage-600" />
              </div>
              <h3 className="font-serif text-3xl font-medium text-charcoal mb-4">Video Library</h3>
              <p className="text-charcoal/60 text-base leading-relaxed mb-8">
                Helpful videos on exercises, stretches, and wellness guidance you can follow along at home.
              </p>
              <div className="space-y-4 mb-8">
                {videos.slice(0, 2).map((video) => (
                  <Link
                    key={video.id}
                    to={`/resources/videos/${video.id}`}
                    className="block text-base text-charcoal hover:text-sage-600 transition-colors font-medium"
                  >
                    {video.title}
                  </Link>
                ))}
              </div>
              <Link
                to="/resources/videos"
                className="inline-flex items-center gap-2 text-sage-600 hover:text-sage-700 font-semibold transition-colors group/link"
                data-testid="videos-link"
              >
                View All Videos
                <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  </section>
);

// Section 9: Final CTA - STRONGER
const FinalCTASection = () => (
  <section className="py-28 md:py-36 lg:py-44 bg-sage-900 relative overflow-hidden" data-testid="final-cta-section">
    {/* Subtle pattern overlay */}
    <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }} />
    
    <div className="container-custom text-center relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-cream-100 mb-8 leading-tight">
          Ready to Take the Next Step?
        </h2>
        <p className="text-cream-200/80 text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
          Start your journey to better health today. Whether you have questions or are ready to book, 
          Ali is here to help.
        </p>
        <div className="flex flex-col sm:flex-row gap-5 justify-center">
          <Button
            asChild
            size="lg"
            className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full px-12 py-7 gap-3 shadow-medium hover:shadow-lg hover:-translate-y-1 transition-all duration-300 text-lg font-semibold"
            data-testid="final-cta-whatsapp"
          >
            <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="w-6 h-6" />
              Message Ali
            </a>
          </Button>
          <Button
            asChild
            size="lg"
            className="bg-white text-charcoal hover:bg-cream-100 rounded-full px-12 py-7 hover:-translate-y-1 transition-all duration-300 text-lg font-semibold"
            data-testid="final-cta-book"
          >
            <Link to="/book">
              <Calendar className="w-6 h-6 mr-2" />
              Book Appointment
            </Link>
          </Button>
        </div>
      </motion.div>
    </div>
  </section>
);

const Home = () => {
  return (
    <>
      <PageMeta
        title="Neuromuscular & Nutritional Therapy in Cobh | Ali Dorgan"
        description="Ali Dorgan offers expert neuromuscular therapy and personalised nutritional support in Cobh, Cork. Helping you recover, perform, and thrive through hands-on treatment and evidence-based nutrition."
      />
      <HeroSection />
      <AuthoritySection />
      <ServicesSection />
      <WhyChooseSection />
      <ConditionsSection />
      <ProcessSection />
      <PricingSection />
      <TestimonialSection />
      <ResourcesSection />
      <FinalCTASection />
    </>
  );
};

export default Home;
